import React from "react";
import LockIcon from "@/assets/icons/lock_hollow.svg?react";
import { CardProps, CardStatus } from "./ConnectionCard";
import { Tooltip } from "antd";

const DATE_PARTS: Intl.DateTimeFormatOptions = {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  timeZone: "UTC",
};

const TIME_PARTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
};

/**
 * True when the value pins an instant rather than just a day.
 *
 * A connection's freshness columns carry mixed precision and the payload cannot
 * label which is which: `date_uploaded` is a real ingest timestamp for anything
 * ingested since vivodb migration 0110, but rows seeded before it — and the
 * static per-tenant feed sets, which are dated from a one-off object listing —
 * only ever knew the day, and reach us as a bare date or as UTC midnight.
 * Printing "00:00" for those would claim a precision nobody measured, so they
 * render as a plain date instead. The cost is that a genuine ingest landing
 * exactly on midnight UTC is shown at day precision, which is imprecise rather
 * than wrong.
 *
 * A bare "YYYY-MM-DD" needs no special case: JS parses a date-only ISO string
 * as UTC midnight, so it falls out here as day-precision too. Every timestamped
 * value the API sends carries an explicit offset (DRF emits "...Z"), so nothing
 * reaching this is parsed as local time.
 */
const hasTimeOfDay = (parsed: Date): boolean =>
  parsed.getUTCHours() !== 0 ||
  parsed.getUTCMinutes() !== 0 ||
  parsed.getUTCSeconds() !== 0;

/** Renders a freshness value at whatever precision it actually carries. */
export const formatConnectionTimestamp = (
  value: string | null | undefined,
): string => {
  if (!value || value.trim() === "") return "N/A";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";

  const date = parsed.toLocaleDateString("en-US", DATE_PARTS);
  if (!hasTimeOfDay(parsed)) return date;

  return `${date} ${parsed.toLocaleTimeString("en-US", TIME_PARTS)} UTC`;
};

export const headerColors = {
  default: "#ECFAFF",
  error: "#FEE",
  locked: "#0000001f",
};

export const icons = {
  default: <div className="rounded-full h-[8px] w-[8px] bg-[#0C98D0]" />,
  error: <div className="rounded-full h-[8px] w-[8px] bg-[#980000]" />,
  locked: <LockIcon />,
};

export const TOOLTIPS = {
  dateUploaded:
    "When the ingestion process finished successfully. This is the point when the data was fully processed and available. Shown in UTC; sources that only report the day they were ingested show no time.",
  fetchFailure: "When the most recent data retrieval attempt failed.",
  dataLocked:
    "The date the dataset was locked for analysis and no further changes are expected.",
  lastChecked:
    "The last time we checked the API or SFTP source for new data. This does not necessarily mean new data was found.",
  lastDataChange:
    "The last time new or changed source data was detected. For manual uploads, this may reflect the source file date, file metadata, or the date we received it.",
};

export const getMessage = (
  status: CardStatus,
  card: CardProps,
): React.ReactNode => {
  switch (status) {
    case "default":
      return (
        <Tooltip title={TOOLTIPS.dateUploaded}>
          <p>{`Uploaded: ${formatConnectionTimestamp(card.date_uploaded)}`}</p>
        </Tooltip>
      );
    case "error":
      return (
        <div className="flex flex-col">
          <Tooltip title={TOOLTIPS.fetchFailure}>
            <p>
              Error occurred on{" "}
              {formatConnectionTimestamp(card.raw_data_fetch_failure_datetime)}
            </p>
          </Tooltip>
          <Tooltip title={TOOLTIPS.dateUploaded}>
            <p>Uploaded {formatConnectionTimestamp(card.date_uploaded)}</p>
          </Tooltip>
        </div>
      );
    case "locked":
      return (
        <Tooltip title={TOOLTIPS.dataLocked}>
          <p>{`Data locked on ${formatConnectionTimestamp(card.data_lock_date)}`}</p>
        </Tooltip>
      );
  }
};
